/**
 * Custom hook for managing drag and drop functionality in the ticket table
 */

import { useState } from 'react';
import { Ticket } from '../../types/ticketTypes';

export interface DragAndDropState {
  draggedTicket: string | null;
  dragOverTicket: string | null;
  dragOverAssignee: string | null;
}

export interface DragAndDropHandlers {
  handleDragStart: (e: React.DragEvent, ticket: Ticket) => void;
  handleDragEnd: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent, ticket: Ticket) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, targetTicket: Ticket) => void;
  handleAssigneeDragOver: (e: React.DragEvent, assignee: string) => void;
  handleAssigneeDragLeave: (e: React.DragEvent) => void;
  handleAssigneeDrop: (e: React.DragEvent, assignee: string) => void;
}

export interface UseDragAndDropProps {
  tickets: Ticket[];
  onTicketUpdate?: (ticket: Ticket) => void;
  onTicketReorder?: (tickets: Ticket[]) => void;
  onTicketReassign?: (ticketId: string, newAssignee: string) => void;
}

export const useDragAndDrop = ({
  tickets,
  onTicketUpdate,
  onTicketReorder,
  onTicketReassign
}: UseDragAndDropProps): DragAndDropState & DragAndDropHandlers => {
  const [draggedTicket, setDraggedTicket] = useState<string | null>(null);
  const [dragOverTicket, setDragOverTicket] = useState<string | null>(null);
  const [dragOverAssignee, setDragOverAssignee] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, ticket: Ticket) => {
    setDraggedTicket(ticket.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', ticket.id);
    
    // Create a custom drag image that's properly aligned
    if (e.currentTarget instanceof HTMLElement) {
      const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-1000px';
      dragImage.style.left = '-1000px';
      dragImage.style.width = e.currentTarget.offsetWidth + 'px';
      dragImage.style.height = e.currentTarget.offsetHeight + 'px';
      dragImage.style.transform = 'none'; // Ensure no rotation
      dragImage.style.opacity = '0.8';
      dragImage.style.pointerEvents = 'none';
      dragImage.style.zIndex = '9999';
      
      document.body.appendChild(dragImage);
      
      // Set the custom drag image
      e.dataTransfer.setDragImage(dragImage, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      
      // Clean up the temporary element after a short delay
      setTimeout(() => {
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage);
        }
      }, 0);
      
      // Add visual feedback to the original element
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedTicket(null);
    setDragOverTicket(null);
    setDragOverAssignee(null);
    
    // Reset visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent, ticket: Ticket) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedTicket && draggedTicket !== ticket.id) {
      setDragOverTicket(ticket.id);
      setDragOverAssignee(null); // Clear assignee drag over when over ticket
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're leaving the ticket row entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverTicket(null);
      setDragOverAssignee(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetTicket: Ticket) => {
    e.preventDefault();
    
    const draggedTicketId = draggedTicket;
    if (!draggedTicketId || draggedTicketId === targetTicket.id) {
      return;
    }

    // Find the dragged ticket
    const draggedTicketObj = tickets.find(t => t.id === draggedTicketId);
    if (!draggedTicketObj) return;

    // If dropping on a ticket in the same assignee group, reorder
    if (draggedTicketObj.assignedTo === targetTicket.assignedTo) {
      // Create new ticket order
      const assigneeTickets = tickets.filter(t => t.assignedTo === targetTicket.assignedTo);
      const otherTickets = tickets.filter(t => t.assignedTo !== targetTicket.assignedTo);
      
      // Remove dragged ticket from its current position
      const filteredAssigneeTickets = assigneeTickets.filter(t => t.id !== draggedTicketId);
      
      // Find target index and insert dragged ticket
      const targetIndex = filteredAssigneeTickets.findIndex(t => t.id === targetTicket.id);
      const newAssigneeTickets = [...filteredAssigneeTickets];
      newAssigneeTickets.splice(targetIndex, 0, draggedTicketObj);
      
      // Combine with other assignee tickets
      const newTickets = [...otherTickets, ...newAssigneeTickets];
      
      onTicketReorder?.(newTickets);
    } else {
      // If dropping on a ticket in a different assignee group, reassign to that assignee
      const updatedTicket = { ...draggedTicketObj, assignedTo: targetTicket.assignedTo };
      onTicketUpdate?.(updatedTicket);
      // Optional callback for additional reassignment logic (notifications, logging, etc.)
      onTicketReassign?.(draggedTicketId, targetTicket.assignedTo);
    }
    
    setDraggedTicket(null);
    setDragOverTicket(null);
    setDragOverAssignee(null);
  };

  // Handlers for dropping on assignee group headers
  const handleAssigneeDragOver = (e: React.DragEvent, assignee: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedTicket) {
      setDragOverAssignee(assignee);
      setDragOverTicket(null); // Clear ticket drag over when over assignee header
    }
  };

  const handleAssigneeDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're leaving the assignee header entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverAssignee(null);
    }
  };

  const handleAssigneeDrop = (e: React.DragEvent, assignee: string) => {
    e.preventDefault();
    
    const draggedTicketId = draggedTicket;
    if (!draggedTicketId) {
      return;
    }

    // Find the dragged ticket
    const draggedTicketObj = tickets.find(t => t.id === draggedTicketId);
    if (!draggedTicketObj) return;

    // Only reassign if dropping on a different assignee
    if (draggedTicketObj.assignedTo !== assignee) {
      const updatedTicket = { ...draggedTicketObj, assignedTo: assignee };
      onTicketUpdate?.(updatedTicket);
      // Optional callback for additional reassignment logic (notifications, logging, etc.)
      onTicketReassign?.(draggedTicketId, assignee);
    }
    
    setDraggedTicket(null);
    setDragOverTicket(null);
    setDragOverAssignee(null);
  };

  return {
    draggedTicket,
    dragOverTicket,
    dragOverAssignee,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleAssigneeDragOver,
    handleAssigneeDragLeave,
    handleAssigneeDrop
  };
};